import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CheckboxField,
  DateTimeField,
  HelperText,
  Theme,
  Typography,
  createStyles,
  makeStyles,
  useSnackbar,
} from '@forgeglobal/design-framework'
import { CampaignEmailWarning } from 'components/campaigns/CampaignEmailWarning'
import { CampaignFormShape } from 'components/campaigns/CampaignForm'
import { Decorator } from 'final-form'
import { OfferingDateDataFragment, OfferingEmailType, useSendDummyEmailMutation } from 'graphql/bindings/AdminBindings'
import { useFormState } from 'react-final-form'
import React from 'react'
import createDecorator from 'final-form-calculate'
import moment from 'moment'

const useStyles = makeStyles(({ spacing }: Theme) =>
  createStyles({
    titleContainer: {
      display: 'flex',
    },
    emailTitle: {
      alignItems: 'center',
      marginLeft: spacing(2),
      display: 'flex',
      justifyContent: 'space-between',
      minWidth: '95%',
    },
    dateFieldContainer: {
      width: '50%',
      maxWidth: spacing(50),
      minWidth: spacing(25),
    },
  })
)

const asFormShape = (allValues: Object | undefined) => (allValues as CampaignFormShape) ?? undefined

export const createDateLogicDecorator: (
  sendMessage: (message: string) => void
) => Decorator<CampaignFormShape> = sendMessage => {
  return createDecorator(
    {
      field: 'available_from',
      updates: (availableFrom, _, allValues, prevValues) => {
        // check for valid previous values (avoid doing this first time through)
        const allForm = asFormShape(allValues)
        const prevForm = asFormShape(prevValues)
        if (!prevForm || !prevForm.available_from) return {}

        // check for a changed value that is valid
        const fromMoment = moment(availableFrom)
        if (allForm.available_from === prevForm.available_from || !fromMoment.isValid()) return {}

        // if checked (now) change reminder email dates
        let newValues: { [key: string]: any } = {}
        const reminderMoment = fromMoment.clone().add(2, 'days')

        if (allForm.is_reminder_email_checked) {
          newValues['auto_reminder_email_at'] = reminderMoment.toISOString()
        }

        if (allForm.is_order_email_checked) {
          newValues['auto_order_reminder_email_at'] = reminderMoment.toISOString()
        }

        if (newValues['auto_reminder_email_at'] || newValues['auto_order_reminder_email_at'])
          sendMessage('Updated automated email reminder times')

        return newValues
      },
    },
    {
      field: 'available_to',
      updates: (availableTo, _, allValues, prevValues) => {
        // check for valid previous values (avoid doing this first time through)
        const allForm = asFormShape(allValues)
        const prevForm = asFormShape(prevValues)
        if (!prevForm || !prevForm.available_to) return {}

        // check for a changed value that is valid
        const toMoment = moment(availableTo)
        if (allForm.available_to === prevForm.available_to || !toMoment.isValid() || !allForm.is_feedback_email_checked)
          return {}

        sendMessage('Updated automated feedback email time')
        return {
          auto_feedback_email_at: toMoment
            .clone()
            .add(1, 'day')
            .toISOString(),
        }
      },
    },
    {
      field: 'is_reminder_email_checked',
      updates: {
        auto_reminder_email_at: (checked, allValues) => {
          const values = allValues as CampaignFormShape

          if (checked && values?.auto_reminder_email_at) {
            // this case is triggered on initial form load
            return values.auto_reminder_email_at
          } else if (checked) {
            return moment(values.available_from)
              .add(2, 'days')
              .toISOString()
          }
          return null
        },
      },
    },
    {
      field: 'is_order_email_checked',
      updates: {
        auto_order_reminder_email_at: (checked, allValues) => {
          const values = allValues as CampaignFormShape
          if (checked && values?.auto_order_reminder_email_at) {
            // this case is triggered on initial form load
            return values.auto_order_reminder_email_at
          } else if (checked) {
            const startPlus2 = moment(values.available_from).add(2, 'days')
            return values?.is_reminder_email_checked
              ? moment.max(startPlus2, moment(values.auto_reminder_email_at)).toISOString()
              : startPlus2.toISOString()
          }
          return null
        },
      },
    },
    {
      field: 'is_feedback_email_checked',
      updates: {
        auto_feedback_email_at: (checked, allValues) => {
          const values = allValues as CampaignFormShape
          if (checked && values?.auto_feedback_email_at) {
            // this case is triggered on initial form load
            return values.auto_feedback_email_at
          } else if (checked) {
            return moment(values.available_to)
              .add(1, 'day')
              .toISOString()
          }
          return null
        },
      },
    }
  ) as Decorator<CampaignFormShape>
}

export const CampaignEmailOptions: React.FC<{
  campaignId: string
  nearbyReminderOfferings: OfferingDateDataFragment[]
  matchingReminderOfferings: OfferingDateDataFragment[]
}> = ({ campaignId, nearbyReminderOfferings, matchingReminderOfferings }) => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const {
    values: { is_reminder_email_checked, is_order_email_checked, is_feedback_email_checked },
  } = useFormState<CampaignFormShape>()
  const [mutate] = useSendDummyEmailMutation()

  const sendEmail = (emailType: OfferingEmailType) => {
    mutate({ variables: { offeringId: campaignId, emailType } }).then(() => {
      enqueueSnackbar('Preview email sent.')
    })
  }

  const sendInvitationEmail = () => sendEmail(OfferingEmailType.Invitation)
  const sendReminderEmail = () => sendEmail(OfferingEmailType.Reminder)
  const sendOrderReminderEmail = () => sendEmail(OfferingEmailType.OrderReminder)
  const sendFeedbackEmail = () => sendEmail(OfferingEmailType.Feedback)

  return (
    <Card>
      <CardHeader
        title={
          <Typography variant="h6" color="secondary">
            Campaign Email Options
          </Typography>
        }
      />
      <CardContent surface="light">
        <Box mb={2} mt={2}>
          <Typography variant="body1" fontWeight="light">
            The invitation email will be sent automatically at the start of the offering. You may choose to
            automatically send optional emails or you may send them manually once the offering is live.
          </Typography>
        </Box>
        <Typography variant="subtitle1">Required campaign emails</Typography>
        <Box mt={2}>
          <Box mb={2} mt={2}>
            <Card>
              <CardHeader
                title={
                  <div className={classes.titleContainer}>
                    <div className={classes.emailTitle}>
                      <Typography variant="subtitle2">Invitation email</Typography>
                      <Typography variant="subtitle2">Automatically send at the start of the offering</Typography>
                    </div>
                  </div>
                }
                action={
                  <Button variant="text" onClick={sendInvitationEmail}>
                    Preview
                  </Button>
                }
              />
            </Card>
          </Box>
        </Box>
        <Typography variant="subtitle1">Optional campaign emails</Typography>
        <Box mt={2}>
          <Box mb={2} mt={2}>
            <Card>
              <CardHeader
                title={
                  <div>
                    <div className={classes.titleContainer}>
                      <div className={classes.emailTitle}>
                        <CheckboxField
                          name="is_reminder_email_checked"
                          label={<Typography variant="subtitle2">Reminder email</Typography>}
                        />
                        {is_reminder_email_checked && (
                          <div className={classes.dateFieldContainer}>
                            <DateTimeField name="auto_reminder_email_at" label="Send at" />
                          </div>
                        )}
                      </div>
                    </div>
                    {nearbyReminderOfferings.length > 0 && (
                      <HelperText>
                        <CampaignEmailWarning
                          content="Warning: Offerings reminders within a 72 hour window may be combined."
                          dateType="reminder"
                          otherOfferings={nearbyReminderOfferings}
                        />
                      </HelperText>
                    )}
                    {matchingReminderOfferings.length > 0 && (
                      <HelperText>
                        <CampaignEmailWarning
                          content="Offering reminder will be combined with the following."
                          dateType="reminder"
                          otherOfferings={matchingReminderOfferings}
                        />
                      </HelperText>
                    )}
                  </div>
                }
                action={
                  <Button variant="text" onClick={sendReminderEmail}>
                    Preview
                  </Button>
                }
              />
            </Card>
          </Box>
          <Box mb={2} mt={2}>
            <Card>
              <CardHeader
                title={
                  <div className={classes.titleContainer}>
                    <div className={classes.emailTitle}>
                      <CheckboxField
                        name="is_order_email_checked"
                        label={<Typography variant="subtitle2">Order reminder email</Typography>}
                      />
                      {is_order_email_checked && (
                        <div className={classes.dateFieldContainer}>
                          <DateTimeField name="auto_order_reminder_email_at" label="Send at" />
                        </div>
                      )}
                    </div>
                  </div>
                }
                action={
                  <Button variant="text" onClick={sendOrderReminderEmail}>
                    Preview
                  </Button>
                }
              />
            </Card>
          </Box>
          <Box mb={2} mt={2}>
            <Card>
              <CardHeader
                title={
                  <div className={classes.titleContainer}>
                    <div className={classes.emailTitle}>
                      <CheckboxField
                        name="is_feedback_email_checked"
                        label={<Typography variant="subtitle2">Feedback email</Typography>}
                      />
                      {is_feedback_email_checked && (
                        <div className={classes.dateFieldContainer}>
                          <DateTimeField name="auto_feedback_email_at" label="Send at" />
                        </div>
                      )}
                    </div>
                  </div>
                }
                action={
                  <Button variant="text" onClick={sendFeedbackEmail}>
                    Preview
                  </Button>
                }
              />
            </Card>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}
